import { useCallback, useState } from 'react';

import type { SyncedBlockFeedbackContext } from '@atlaskit/editor-plugin-synced-block/synced-block-plugin-type';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react/function-with-condition';

import { expValEquals } from '@confluence/feature-experiments';

type SyncedBlockFeedbackControls = {
	blockType: SyncedBlockFeedbackContext['blockType'];
	isOpen: boolean;
	onClose: () => void;
	onGiveFeedback: ((context: SyncedBlockFeedbackContext) => void) | undefined;
};

const disabledSyncedBlockFeedbackControls: SyncedBlockFeedbackControls = {
	blockType: 'reference',
	isOpen: false,
	onClose: () => {},
	onGiveFeedback: undefined,
};

const useEnabledSyncedBlockFeedback = (): SyncedBlockFeedbackControls => {
	const [isOpen, setIsOpen] = useState(false);
	const [blockType, setBlockType] = useState<SyncedBlockFeedbackContext['blockType']>('reference');

	const onGiveFeedback = useCallback(({ blockType: nextBlockType }: SyncedBlockFeedbackContext) => {
		setBlockType(nextBlockType);
		setIsOpen(true);
	}, []);
	const onClose = useCallback(() => setIsOpen(false), []);

	return { blockType, isOpen, onClose, onGiveFeedback };
};

const useDisabledSyncedBlockFeedback = (): SyncedBlockFeedbackControls =>
	disabledSyncedBlockFeedbackControls;

export const useSyncedBlockFeedback: () => SyncedBlockFeedbackControls = functionWithCondition(
	() => expValEquals('platform_editor_sync_block_activation', 'isEnabled', true),
	useEnabledSyncedBlockFeedback,
	useDisabledSyncedBlockFeedback,
);
